import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useSalaryProfile() {
  return useQuery({
    queryKey: ["salary-profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [profileData, experienceData, educationData, skillsData] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", user.id).single(),
        supabase.from("work_experiences").select("*").eq("user_id", user.id).order("start_date", { ascending: false }),
        supabase.from("educations").select("*").eq("user_id", user.id).order("end_year", { ascending: false }),
        supabase.from("skills").select("*").eq("user_id", user.id),
      ]);

      const profile = profileData.data;
      const experiences = experienceData.data || [];
      const education = educationData.data || [];
      const skills = skillsData.data || [];

      // Calculate years of experience
      const totalYears = experiences.reduce((acc, exp) => {
        const start = new Date(exp.start_date);
        const end = exp.is_current ? new Date() : new Date(exp.end_date || new Date());
        const years = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365);
        return acc + years;
      }, 0);

      // Get current job title
      const currentJob = experiences.find(exp => exp.is_current);
      const jobTitle = currentJob?.title || profile?.headline || "";

      // Get location
      const location = profile?.location || "";

      // Get highest education level
      const educationLevel = education.length > 0 
        ? education[0].degree 
        : "";

      // Get industry from most recent job
      const industry = experiences.length > 0 ? experiences[0].company : "";

      return {
        profile,
        experiences,
        education,
        skills: skills.map(s => s.name),
        yearsExperience: Math.floor(totalYears),
        jobTitle,
        location,
        educationLevel,
        industry,
      };
    },
  });
}
